import { Button, Chip, IconButton, LinearProgress, Tooltip, Typography } from '@mui/material';
import { Ban, Check, Copy, Download } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { triggerDownload } from '../../../utils/download';
import { getFileBlob } from '../api/generateVideo.api';
import { isActiveJob } from '../hooks/useVideoJobs';
import type { VideoJob, VideoJobStatus } from '../types';
import { formatBytes, formatNumber } from '../utils';
import { VideoJobPlayer } from './VideoJobPlayer';
import { VideoShareButton } from './VideoShareButton';

const STATUS_COLORS: Record<VideoJobStatus, 'default' | 'info' | 'success' | 'error' | 'warning'> =
  {
    queued: 'default',
    running: 'info',
    completed: 'success',
    failed: 'error',
    cancelled: 'warning',
  };

interface VideoJobCardProps {
  job: VideoJob;
  onCancel: (jobId: string) => Promise<void>;
}

export const VideoJobCard: React.FC<VideoJobCardProps> = ({ job, onCancel }) => {
  const { t, i18n } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const active = isActiveJob(job);
  const artifact = job.result?.artifact;

  const download = async () => {
    if (!artifact) return;
    setBusy(true);
    try {
      const objectUrl = URL.createObjectURL(await getFileBlob(artifact.downloadUrl));
      triggerDownload(objectUrl, artifact.fileName);
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
    } finally {
      setBusy(false);
    }
  };

  const copyPrompt = async () => {
    if (!job.prompt) return;
    try {
      await navigator.clipboard.writeText(job.prompt);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard can be blocked (insecure context / permissions); nothing to recover.
    }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await onCancel(job.jobId);
    } finally {
      setBusy(false);
    }
  };

  const meta = [
    t(
      `services.generateVideo.operations.${job.operation === 'image_to_video' ? 'imageToVideo' : 'textToVideo'}`
    ),
    job.modelCode,
    job.resolutionLabel,
    job.durationSeconds
      ? t('services.generateVideo.seconds', { value: job.durationSeconds })
      : null,
    job.aspectRatio,
  ].filter(Boolean);

  return (
    <article className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Typography
            variant="caption"
            className="block truncate text-gray-500 dark:text-slate-400"
          >
            {meta.join(' · ')}
          </Typography>
          <Typography variant="caption" className="text-gray-400 dark:text-slate-500">
            {new Date(job.createdAt).toLocaleString(i18n.language)}
          </Typography>
        </div>
        <Chip
          size="small"
          color={STATUS_COLORS[job.status]}
          label={t(`services.generateVideo.status.${job.status}`)}
        />
      </header>

      {job.prompt && (
        <div className="flex items-start gap-1">
          <Typography
            variant="body2"
            className="line-clamp-2 min-w-0 flex-1 break-words text-gray-800 dark:text-slate-200"
          >
            {job.prompt}
          </Typography>
          <Tooltip
            title={t(
              copied ? 'services.generateVideo.copied' : 'services.generateVideo.copyPrompt'
            )}
          >
            <IconButton
              size="small"
              aria-label={t('services.generateVideo.copyPrompt')}
              onClick={() => void copyPrompt()}
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </IconButton>
          </Tooltip>
        </div>
      )}

      {active && (
        <div className="space-y-1">
          <LinearProgress />
          <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
            {job.cancelRequested
              ? t('services.generateVideo.cancelPending')
              : t('services.generateVideo.inProgressHint')}
          </Typography>
        </div>
      )}

      {artifact && <VideoJobPlayer artifact={artifact} aspectRatio={job.aspectRatio} />}

      {!artifact && job.status === 'completed' && job.expiresAt && (
        <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
          {t('services.generateVideo.expired')}
        </Typography>
      )}

      {job.error && (
        <Typography variant="body2" className="text-red-600 dark:text-red-400">
          {t(`services.generateVideo.errors.${job.error.code}`, {
            defaultValue: job.error.message,
          })}
        </Typography>
      )}

      <footer className="flex flex-wrap items-center justify-between gap-2">
        <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
          {job.chargedTokens !== null
            ? t('services.generateVideo.charged', {
                value: formatNumber(job.chargedTokens, i18n.language),
              })
            : t('services.generateVideo.estimated', {
                value: formatNumber(job.estimatedTokens ?? 0, i18n.language),
              })}
          {artifact?.fileSize ? ` · ${formatBytes(artifact.fileSize, i18n.language)}` : ''}
          {artifact && job.expiresAt
            ? ` · ${t('services.generateVideo.expiresOn', {
                date: new Date(job.expiresAt).toLocaleDateString(i18n.language),
              })}`
            : ''}
        </Typography>
        <div className="flex gap-2">
          {active && !job.cancelRequested && (
            <Button
              size="small"
              color="warning"
              startIcon={<Ban size={16} />}
              disabled={busy}
              onClick={cancel}
            >
              {t('services.generateVideo.cancel')}
            </Button>
          )}
          {artifact && <VideoShareButton artifact={artifact} prompt={job.prompt} disabled={busy} />}
          {artifact && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Download size={16} />}
              disabled={busy}
              onClick={download}
            >
              {t('services.generateVideo.download')}
            </Button>
          )}
        </div>
      </footer>
    </article>
  );
};
